import type { ProseKinds } from '../ProseRender';
import type { RendererProps } from '../../../domain/RendererContract';
import { PROSE_TEXT_ELEMENT_ID, isQuoteBoundProseKind } from '@/blocks/domain/helpers/proseElements';
import { resolveTokens } from '../../../application/resolveTokens';
import { resolveProseSegments } from '../../../application/resolveProseSegments';
import { keepsUnboundTokens } from '../../../application/unboundTokenHtml';
import { useQuoteCanvasEditing, type QuoteCanvasSlice } from '@/document-render/presentation/editing/quote/QuoteCanvasEditingContext';
import { useHasEditableDocument, useOptionalEditableDocument } from '@/document-render/presentation/editing/EditableDocumentContext';
import { useCanvasSelection } from '@/document-render/presentation/editing/useCanvasSelection';
import { useDocumentSurface } from '@/document-render/presentation/primitives/useDocumentSurface';

const PROSE_MIN_HEIGHT_PX = 50;

export type ProseRenderMode = 'static' | 'quote' | 'canvas';

export interface ProseRenderModel {
  mode: ProseRenderMode;
  /** The quote canvas's prose slice (`mode === 'quote'`). */
  quote: Extract<QuoteCanvasSlice, { kind: 'prose' }> | null;
  /** A later page slice that steps aside because the first slice carries the whole body (open editor or placeholder). */
  hidden: boolean;
  isFirstSlice: boolean;
  title: string;
  bodyHtml: string;
  resolvedTitle: string;
  visibleHtml: string;
  /** Sample data off in the editor: dynamic values stay as token chips (title and body) instead of resolving to blanks. */
  keepTokens: boolean;
  showTitle: boolean;
  showContinuedLabel: boolean;
  minHeight: number;
  accent: string;
}

/**
 * Everything `ProseRender` decides before it draws (LLAMS-610 DB-05): which surface it is on —
 * `static` (template preview, quote detail, PDF snapshot, measurement), `quote` (the quote canvas's
 * in-place editor) or `canvas` (the block editor's selectable Heading / Text) — and the resolved,
 * sliced content. Substituted values are HTML-escaped because the body is injected as HTML.
 */
export function useProseRenderMode({
  content,
  context,
  style,
  accentColor,
  knownPaths,
  slice
}: RendererProps<ProseKinds>): ProseRenderModel {
  const quoteSlice = useQuoteCanvasEditing();
  const hasEditable = useHasEditableDocument();
  const editable = useOptionalEditableDocument();
  const surface = useDocumentSurface();
  const textSelection = useCanvasSelection(PROSE_TEXT_ELEMENT_ID);

  const keepTokens = keepsUnboundTokens(context, surface);
  const resolveOptions =
    knownPaths || keepTokens ? { ...(knownPaths ? { knownPaths } : {}), ...(keepTokens ? { keepTokens } : {}) } : undefined;
  const title = typeof content.title === 'string' ? content.title : '';
  const bodyHtml = typeof content.bodyHtml === 'string' ? content.bodyHtml : '';
  const resolvedTitle = resolveTokens(title, context, resolveOptions).trim();
  const segments = resolveProseSegments(bodyHtml, context, resolveOptions);
  const visibleHtml = segments.slice(slice?.startIndex ?? 0, slice?.endIndex ?? segments.length).join('');

  const quote = quoteSlice?.kind === 'prose' ? quoteSlice : null;
  const canvas = !quote && hasEditable && surface !== 'export';
  const quoteBound = canvas && isQuoteBoundProseKind(content.kind);
  const isFirstSlice = !slice || slice.startIndex === 0;
  const canvasEditingText = canvas && textSelection.isSelected && Boolean(editable?.renderProseEditor);
  const ownedByFirstSlice = Boolean(quote?.proseEditor?.isEditing) || quoteBound || canvasEditingText;

  let mode: ProseRenderMode = 'static';
  if (quote) mode = 'quote';
  else if (canvas) mode = 'canvas';

  return {
    mode,
    quote,
    hidden: !isFirstSlice && ownedByFirstSlice,
    isFirstSlice,
    title,
    bodyHtml,
    resolvedTitle,
    visibleHtml,
    keepTokens,
    showTitle: Boolean(resolvedTitle) && isFirstSlice,
    showContinuedLabel: Boolean(resolvedTitle) && Boolean(slice?.isContinuation),
    // A constant floor so an empty body keeps a hit area. The block's real height is MEASURED from
    // its rendered box (`useBlockHeightMeasurement`), never guessed from the HTML's character count —
    // tag-heavy prose used to inflate its box and shift pagination (LLAMS-610 DB-06).
    minHeight: PROSE_MIN_HEIGHT_PX,
    accent: accentColor ?? style.textColor
  };
}
